'use client';
import React from 'react';
import { cn } from '@lib/utils';
import { Card } from '@components/ui/card';

const steps = [
  {
    step: 'Step 1',
    name: 'Upload photos',
    description: 'Upload at least 8 photos of yourself that show your best self.',
    // imagePath: 'https://imagedelivery.net/9sCnq8t6WEGNay0RAQNdvQ/af2d8ac2-e429-4809-db62-0f31b1f04100/public'
    // imagePath: 'https://imagedelivery.net/9sCnq8t6WEGNay0RAQNdvQ/58f5169f-d762-46b6-8e5f-dcc16474b000/public'
    imagePath: 'https://imagedelivery.net/9sCnq8t6WEGNay0RAQNdvQ/091c5f31-7343-41af-b0ab-5ee860ecbe00/public'
  },
  {
    step: 'Step 2',
    name: 'Pick a style',
    description: 'Choose from our curated collection of styles that match your vibe and help you shine.',
    // imagePath: 'https://imagedelivery.net/9sCnq8t6WEGNay0RAQNdvQ/42bbe197-285b-4e6d-b029-3f383e292d00/public'
    // imagePath: 'https://imagedelivery.net/9sCnq8t6WEGNay0RAQNdvQ/93560cf6-03d4-4be9-ae31-6caf6e2d5300/public'
    imagePath: 'https://imagedelivery.net/9sCnq8t6WEGNay0RAQNdvQ/2a2706f0-a332-4c65-c5ec-190f9a6f8c00/public'
  },
  {
    step: 'Step 3',
    name: 'Wait for photos',
    description: 'Hang tight while we process your images and create your custom portraits.',
    // imagePath: 'https://imagedelivery.net/9sCnq8t6WEGNay0RAQNdvQ/28888494-6aa0-40cd-3213-a54947dd8c00/public'
    // imagePath: 'https://imagedelivery.net/9sCnq8t6WEGNay0RAQNdvQ/73b2bfc7-2314-4920-4e0b-875b0af6bc00/public'
    imagePath: 'https://imagedelivery.net/9sCnq8t6WEGNay0RAQNdvQ/7d9c5468-c686-4f93-d7ac-da8dfb6e1200/public'
  },
  {
    step: 'Step 4',
    name: 'Download and share',
    description: 'Once your portraits are ready, download them and share with friends and family.',
    // imagePath: 'https://imagedelivery.net/9sCnq8t6WEGNay0RAQNdvQ/cbe5603d-00e1-4353-e4d5-be77c7474c00/public'
    imagePath: 'https://imagedelivery.net/9sCnq8t6WEGNay0RAQNdvQ/1887dc8c-b270-4457-682b-fc7446675e00/public'
  }
];

interface HowItWorksV2Props {
  className?: string;
}

export default function HowItWorksV2({ className }: HowItWorksV2Props) {
  // Generate HowTo schema markup
  const howToSchema = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: 'How to Create AI-Generated Professional Portraits with PicStudio.AI',
    description:
      'Learn how to create stunning professional portraits using AI technology. Get photos generated in seconds, not days with our simple 4-step process.',
    image: [
      'https://imagedelivery.net/9sCnq8t6WEGNay0RAQNdvQ/091c5f31-7343-41af-b0ab-5ee860ecbe00/public',
      'https://imagedelivery.net/9sCnq8t6WEGNay0RAQNdvQ/2a2706f0-a332-4c65-c5ec-190f9a6f8c00/public',
      'https://imagedelivery.net/9sCnq8t6WEGNay0RAQNdvQ/7d9c5468-c686-4f93-d7ac-da8dfb6e1200/public',
      'https://imagedelivery.net/9sCnq8t6WEGNay0RAQNdvQ/1887dc8c-b270-4457-682b-fc7446675e00/public'
    ],
    totalTime: 'PT15M',
    estimatedCost: {
      '@type': 'MonetaryAmount',
      currency: 'USD',
      value: '29.99'
    },
    supply: [
      {
        '@type': 'HowToSupply',
        name: 'Personal Photos',
        description: 'At least 8 high-quality photos of yourself showing your best self'
      }
    ],
    tool: [
      {
        '@type': 'HowToTool',
        name: 'PicStudio.AI Platform',
        description: 'AI-powered portrait generation platform'
      },
      {
        '@type': 'HowToTool',
        name: 'Web Browser',
        description: 'Any modern web browser to access the platform'
      }
    ],
    step: steps.map((step, index) => ({
      '@type': 'HowToStep',
      position: index + 1,
      name: step.name,
      text: step.description,
      image: {
        '@type': 'ImageObject',
        url: step.imagePath,
        description: step.name
      }
    }))
  };

  return (
    <>
      {/* HowTo Schema Markup */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(howToSchema) }} />

      <section className={cn('p-0 bg-transparent border-none py-12', className)}>
        <div className="max-w-2xl mb-6">
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight">
            Get photos generated{' '}
            <span className="bg-gradient-to-r from-[#5E83ED] via-[#AA73AC] to-[#C96D78] bg-clip-text text-transparent">
              in seconds
            </span>
            , not days
          </h2>
          <p className="text-lg leading-8 text-muted-foreground">
            It only takes a few clicks to generate a photos of yourself.
          </p>
        </div>

        <ol className="grid grid-cols-1 lg:grid-cols-4 gap-12 lg:gap-8 list-none">
          {steps.map((step, index) => (
            <li key={index} className="relative flex flex-col gap-2">
              <Card className="p-2 lg:p-4 mb-2 w-full">
                <img
                  src={step.imagePath}
                  alt={`Step ${index + 1}: ${step.name}`}
                  className="rounded-lg"
                  loading="lazy"
                />
              </Card>
              <div className="flex flex-col gap-2 text-left">
                <dl className="flex flex-col gap-2">
                  <dt className="text-base flex items-center gap-2 font-semibold">
                    <div
                      className="rounded-full aspect-square w-5 h-5 flex items-center justify-center bg-accent text-[11px] font-bold p-0 text-white"
                      aria-label={`Step ${index + 1}`}
                    >
                      {index + 1}
                    </div>
                    {step.name}
                  </dt>
                  <dd className="text-sm text-muted-foreground">{step.description}</dd>
                </dl>
              </div>
            </li>
          ))}
        </ol>
      </section>
    </>
  );
}
